import { Children, isValidElement, type ReactElement, type ReactNode, useRef, useState } from "react";
import type { LayoutChangeEvent } from "react-native";
import RNCarousel, { type ICarouselInstance } from "react-native-reanimated-carousel";
import { isWeb } from "tamagui";
import { animateOnly } from "../Motion/motion";
import { XStack, YStack } from "../Stacks";
export type CarouselProps = {
  /** Milliseconds between auto-advances. Set to 0 to disable. @default 3000 */
  autoRotateMs?: number;
  /** Fires whenever the active slide index changes. */
  onIndexChange?: (index: number) => void;
  /** Each direct child is rendered as one slide. */
  children: ReactNode;
};

/**
 * Carousel with auto-rotate and dot pagination. Each direct child is one slide;
 * slide content is the consumer's responsibility. The component sizes itself to
 * the tallest slide so swiping doesn't reflow.
 */
export function Carousel({
  autoRotateMs = 3000,
  onIndexChange,
  children
}: CarouselProps) {
  const slides = Children.toArray(children).filter(isValidElement);
  const count = slides.length;
  const [width, setWidth] = useState(0);
  // Height: native uses the onLayout wrapper; web measures tallest slide via offscreen probes.
  const [nativeHeight, setNativeHeight] = useState(0);
  const [probeHeights, setProbeHeights] = useState<Record<number, number>>({});
  const [activeIndex, setActiveIndex] = useState(0);
  const carouselRef = useRef<ICarouselInstance>(null);
  // Mirrors `activeIndex` for comparisons inside the worklet-bridged progress
  // callback, which would otherwise see stale closure state between renders.
  const activeIndexRef = useRef(0);
  const handleLayout = (event: LayoutChangeEvent) => {
    const {
      width: w,
      height: h
    } = event.nativeEvent.layout;
    setWidth(w);
    if (!isWeb) {
      setNativeHeight(h);
    }
  };
  const handleProbeLayout = (index: number) => (event: LayoutChangeEvent) => {
    const h = event.nativeEvent.layout.height;
    setProbeHeights(prev => prev[index] === h ? prev : {
      ...prev,
      [index]: h
    });
  };

  // Use the tallest measured slide so swiping doesn't resize the carousel.
  const probeHeight = (() => {
    const measured = Object.values(probeHeights);
    if (measured.length < count) {
      return 0;
    }
    return Math.max(0, ...measured);
  })();

  // Flip at the 50% swipe threshold so dots/onIndexChange follow the visible slide, not the snap.
  const handleProgressChange = (_offset: number, absoluteProgress: number) => {
    if (count === 0) {
      return;
    }
    const next = (Math.round(absoluteProgress) + count) % count;
    if (next !== activeIndexRef.current) {
      activeIndexRef.current = next;
      setActiveIndex(next);
      onIndexChange?.(next);
    }
  };
  const goTo = (target: number) => {
    if (target === activeIndex || target < 0 || target >= count) {
      return;
    }
    carouselRef.current?.scrollTo({
      index: target,
      animated: true
    });
  };
  const height = isWeb ? probeHeight : nativeHeight;
  const canRender = width > 0 && height > 0 && count > 0;
  const fillProps = isWeb ? {} : {
    flex: 1,
    minHeight: 0
  };
  return <YStack width="100%" {...fillProps} data-sentry-element="YStack" data-sentry-component="Carousel" data-sentry-source-file="Carousel.tsx">
      <YStack width="100%" onLayout={handleLayout} {...fillProps} data-sentry-element="YStack" data-sentry-source-file="Carousel.tsx">
        {/* Web-only: offscreen probes measure each slide's natural height since the
            carousel needs explicit dimensions and there's no flex ancestor on web. */}
        {isWeb && width > 0 ? slides.map((slide, idx) => <YStack key={idx} position="absolute" opacity={0} pointerEvents="none" width={width} onLayout={handleProbeLayout(idx)}>
                {slide}
              </YStack>) : null}
        {canRender ? <RNCarousel<ReactElement> ref={carouselRef} style={{
        width,
        height
      }} data={slides} loop={count > 1} autoPlay={count > 1 ? autoRotateMs > 0 : false} autoPlayInterval={autoRotateMs} onProgressChange={handleProgressChange} renderItem={({
        item
      }) => item} /> : null}
      </YStack>
      <XStack gap="$sp1" jc="center" paddingVertical="$sp3" data-sentry-element="XStack" data-sentry-source-file="Carousel.tsx">
        {slides.map((_, idx) => <Dot key={idx} active={idx === activeIndex} onPress={() => goTo(idx)} />)}
      </XStack>
    </YStack>;
}
function Dot({
  active,
  onPress
}: {
  active: boolean;
  onPress: () => void;
}) {
  return <YStack testID="carousel.dot" borderRadius="$brFull" transition="expand" animateOnly={animateOnly("width", "backgroundColor")} width={active ? "$si3" : "$si1"} height="$si1" onPress={onPress} backgroundColor={active ? "$surfaceStrong" : "$color6"} cursor="pointer" data-sentry-element="YStack" data-sentry-component="Dot" data-sentry-source-file="Carousel.tsx" />;
}